import React, { useState } from 'react';

import { useSnackbar } from 'notistack';
import { useSelector } from 'react-redux';

import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
} from '@mui/material';

import { useCopyTourOfferContent } from '~/hooks/apis/tours/useTourOffers';

import { customerPortalHost } from '~/services/common';

import { TourOffer, TourOfferRelatedTour } from '~/types/tours/tourOffer';

import NativeTourOfferPicker from './NativeTourOfferPicker';

interface Props {
  offer: TourOffer;
  legacyTourId: string;
  /** Copying replaces what's saved, so it's blocked while there are unsaved edits. */
  hasUnsavedChanges: boolean;
  onCopied: (offer: TourOffer) => void;
}

/** Preview the tour on the customer site, or copy another tour's content into it. */
export default function NativeTourHeaderActions({ offer, legacyTourId, hasUnsavedChanges, onCopied }: Props) {
  const { enqueueSnackbar } = useSnackbar();
  const tenant = useSelector((state: App.State) => state.tenant);
  const copyContent = useCopyTourOfferContent(offer.id);
  const [isOpen, setIsOpen] = useState(false);
  const [from, setFrom] = useState<TourOfferRelatedTour | null>(null);

  const close = () => {
    setIsOpen(false);
    setFrom(null);
  };

  const copy = async () => {
    if (!from) {
      return;
    }
    try {
      const updated = await copyContent.mutateAsync(from.tourOfferId);
      onCopied(updated);
      enqueueSnackbar(`Content copied from ${from.name ?? 'the tour'}`, { variant: 'success' });
      close();
    } catch (error) {
      enqueueSnackbar(`Couldn't copy the content: ${error.message}`, { variant: 'error' });
    }
  };

  return (
    <>
      <Stack direction="row" spacing={1}>
        <Button variant="text" onClick={() => setIsOpen(true)}>
          Copy content from another tour
        </Button>
        <Button
          variant="text"
          endIcon={<OpenInNewIcon fontSize="small" />}
          href={`${customerPortalHost(tenant)}/tour/tour-${legacyTourId}`}
          target="_blank"
          rel="noreferrer"
        >
          Preview on {tenant.title}
        </Button>
      </Stack>
      <Dialog open={isOpen} onClose={close} maxWidth="sm" fullWidth>
        <DialogTitle>Copy content from another tour</DialogTitle>
        <DialogContent>
          <Stack spacing={2} pt={1}>
            <DialogContentText>
              Replaces this tour's name, brand, content, deposit and payment templates, fine print and recommended
              hotels with the other tour's, and saves straight away. Each option with the same name also takes its copy,
              inclusions, package inclusions, itinerary, route map, ages, pax and private request, and its images if it
              has none. Departures and prices stay as they are.
            </DialogContentText>
            {hasUnsavedChanges && (
              <Alert severity="warning">Save or discard your changes first. Copying would lose them.</Alert>
            )}
            <NativeTourOfferPicker label="Copy from" value={from} excludeIds={[offer.id]} onChange={setFrom} />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button variant="text" onClick={close}>
            Cancel
          </Button>
          <Button variant="contained" disabled={!from || hasUnsavedChanges || copyContent.isPending} onClick={copy}>
            Copy and save
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
